// React
import { ReactElement } from 'react';
// Next
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
// Design System
import { Flag } from '@utmb/design-system/flag/Flag';
import { Font } from '@utmb/design-system/text/font';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { Placeholder } from '@utmb/design-system/placeholder/Placeholder';
// Interfaces
import RacePageView from '@utmb/types/view/RacePageView';
// Styles
import styles from './race-header.module.scss';
import { formatUnit } from '@utmb/utils/unit';
import { usePreference } from '@utmb/contexts/PreferenceContext';

const RaceHeader = (props: RacePageView): ReactElement => {
    const { startCountry, startPlace, startCountryCode, name, date, elevationGain, distance, piCategory, logo } = props;

    const {
        preferences: { preferredUnit },
    } = usePreference();

    const { t } = useTranslation();

    return (
        <div className={`container ${styles.rh_root}`}>
            <div className={`row ${styles.rh_content_container}`}>
                <div className={`col-12 col-lg-8 order-1 order-lg-0 ${styles.rh_stats_container}`}>
                    {name && (
                        <Font.FuturaBold as="h1" mobile="24" desktop="34" className={styles.rh_race_title}>
                            {name}
                        </Font.FuturaBold>
                    )}
                    <div className="row">
                        <div className={`col-12 col-md-6 col-lg-4 ${styles.rh_stat_wrapper}`}>
                            <Font.OxaniumBold mobile="14" desktop="16" className={styles.rh_stat_label}>
                                {t('raceRanking.cityLabel')}
                            </Font.OxaniumBold>
                            {startCountry && startCountryCode ? (
                                <Font.OxaniumBold mobile="18" desktop="22">
                                    <Flag isoCode={startCountryCode} />
                                    <span className={styles.rh_start_point}>{startPlace !== '' ? startPlace : startCountry}</span>
                                </Font.OxaniumBold>
                            ) : (
                                <Font.OxaniumBold mobile="18" desktop="22" as="span">
                                    -
                                </Font.OxaniumBold>
                            )}
                        </div>
                        <div className={`col-12 col-md-6 col-lg-4 ${styles.rh_stat_wrapper}`}>
                            <Font.OxaniumBold mobile="14" desktop="16" className={styles.rh_stat_label}>
                                {t('raceRanking.dateLabel')}
                            </Font.OxaniumBold>
                            {date ? (
                                <Font.OxaniumBold mobile="18" desktop="22" as="span">
                                    {date}
                                </Font.OxaniumBold>
                            ) : (
                                <Font.OxaniumBold mobile="18" desktop="22" as="span">
                                    -
                                </Font.OxaniumBold>
                            )}
                        </div>
                        <div className={`col-12 col-md-6 col-lg-4 ${styles.rh_stat_wrapper}`}>
                            <Font.OxaniumBold mobile="14" desktop="16" className={styles.rh_stat_label}>
                                {t('raceRanking.categoryLabel')}
                            </Font.OxaniumBold>
                            {piCategory ? (
                                <div className={styles.rh_category_logo_container}>
                                    <PiCategoryLogo piCategory={piCategory} />
                                </div>
                            ) : (
                                <Font.OxaniumBold mobile="18" desktop="22" as="span">
                                    -
                                </Font.OxaniumBold>
                            )}
                        </div>
                        <div className={`col-12 col-md-6 col-lg-4 ${styles.rh_stat_wrapper}`}>
                            <Font.OxaniumBold mobile="14" desktop="16" className={styles.rh_stat_label}>
                                {t('raceRanking.distanceLabel')}
                            </Font.OxaniumBold>
                            {distance ? (
                                <Font.OxaniumBold mobile="18" desktop="22" as="span">
                                    {formatUnit(preferredUnit, 'distance', distance)}
                                </Font.OxaniumBold>
                            ) : (
                                <Font.OxaniumBold mobile="18" desktop="22" as="span">
                                    -
                                </Font.OxaniumBold>
                            )}
                        </div>
                        <div className={`col-12 col-md-6 col-lg-4 ${styles.rh_stat_wrapper}`}>
                            <Font.OxaniumBold mobile="14" desktop="16" className={styles.rh_stat_label}>
                                {t('raceRanking.elevationLabel')}
                            </Font.OxaniumBold>
                            {elevationGain ? (
                                <Font.OxaniumBold mobile="18" desktop="22" as="span">
                                    {formatUnit(preferredUnit, 'elevationGain', elevationGain)}
                                </Font.OxaniumBold>
                            ) : (
                                <Font.OxaniumBold mobile="18" desktop="22" as="span">
                                    -
                                </Font.OxaniumBold>
                            )}
                        </div>
                    </div>
                </div>
                <div className={`col-12 col-lg-4 order-0 order-lg-1 ${styles.rh_logo_wrapper}`}>
                    {logo ? (
                        <Image alt={name} src={logo} width={300} height={300} />
                    ) : (
                        <div className={styles.rh_placeholder_wrapper}>
                            <Placeholder width="300px" height="300px" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RaceHeader;
